import { AGREEMENT_PERMISSIONS } from './agreementPermissions'
import { BILL_PERMISSIONS } from './billPermissions'
import { FGA_VERBS_BY_RELATION } from './generated/model'
import { REQUEST_PERMISSIONS } from './requestPermissions'
import { SUPPLIER_ORGANISATION_PERMISSIONS, SUPPLIER_PERMISSIONS } from './supplierPermissions'
import { WORKFLOW_PERMISSIONS } from './workflowPermissions'
import type { CoreDataDimension } from './coreDataDimensions'
import type { FgaAssignableRelation } from './generated/model'

/**
 * The entities a permission set can govern, and the entities the per-record access API serves. A
 * set's entity is fixed at creation.
 */
export const PERMISSION_SET_ENTITIES = ['request', 'supplier', 'agreement', 'bill', 'workflow'] as const
export type PermissionSetEntity = (typeof PERMISSION_SET_ENTITIES)[number]

/** The permission vocabulary of each entity, keyed by entity. */
export const PERMISSIONS_BY_ENTITY = {
  request: REQUEST_PERMISSIONS,
  supplier: SUPPLIER_PERMISSIONS,
  agreement: AGREEMENT_PERMISSIONS,
  bill: BILL_PERMISSIONS,
  workflow: WORKFLOW_PERMISSIONS,
} as const satisfies Record<PermissionSetEntity, readonly string[]>

/**
 * A dimension may only be listed for an entity whose `derived_scope` can hold that dimension's
 * values — a grant on a dimension the entity carries no edge for writes a well-formed tuple that
 * resolves to nothing. A bill or workflow has no scope edge at all, so it accepts none.
 */
export const SCOPE_DIMENSIONS_BY_ENTITY = {
  request: ['department', 'subsidiary'],
  supplier: ['department', 'subsidiary'],
  agreement: ['department', 'subsidiary'],
  bill: [],
  workflow: [],
} as const satisfies Record<PermissionSetEntity, readonly CoreDataDimension[]>

/**
 * How many changes the grant endpoints accept in one batch. Each change costs several sequential
 * OpenFGA round-trips to converge, so an unbounded batch would run into the lambda timeout mid-write
 * — a partial save — rather than being rejected cleanly.
 */
export const MAX_GRANT_CHANGES_PER_BATCH = 50

/**
 * The most records one batched permission read answers for, whatever the entity: the size it was load-tested
 * at. A longer list page splits into reads of this size and sends them in parallel, which load-tested as fast
 * as one read, so a larger cap would save requests rather than time.
 */
export const MAX_RECORDS_PER_PERMISSION_READ = 40

/**
 * The relations naming who takes part in a record, per entity — the model's `explicit_view` minus
 * the grant and supplier read-through arms, which are not held on the record itself.
 *
 * An agreement lists owners only: the supplier's contacts read through to it, but that access is
 * held on the supplier, so an access API cannot revoke it there.
 *
 * Named rather than derived, so a relation the model gains is a decision about whether it confers
 * revocable access. `modelWiring.test.ts` fails until that decision is made.
 */
export const PARTICIPANT_RELATIONS_BY_ENTITY = {
  request: ['derived_requester', 'derived_collaborator', 'derived_rfx_owner'],
  supplier: ['derived_contact'],
  agreement: ['derived_owner'],
  bill: ['derived_approver', 'derived_draft_assignee', 'derived_mention'],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly FgaAssignableRelation<Entity>[] }

/** Any relation naming a participant, across every entity. */
export type ParticipantRelation = (typeof PARTICIPANT_RELATIONS_BY_ENTITY)[PermissionSetEntity][number]

/**
 * A permission of the named entity. Callers holding a runtime entity get the union; use the guard to
 * narrow.
 */
export type PermissionOf<Entity extends PermissionSetEntity> = (typeof PERMISSIONS_BY_ENTITY)[Entity][number]

export type PermissionSetPermission = PermissionOf<PermissionSetEntity>

/** Permissions no set may hold until the organisation has RELEASE_TPRM_LEVELS. */
export const TPRM_LEVELS_PERMISSIONS: readonly PermissionSetPermission[] = ['edit_tprm']

/**
 * Whether the permission belongs to the entity's vocabulary. The entities' permissions overlap in
 * shape but not in meaning, and one sent under the wrong entity is rejected by the permissions API, so
 * anything crossing that boundary has to be checked rather than assumed.
 */
export const isPermissionOfEntity = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permission: string
): permission is PermissionOf<Entity> => {
  const vocabulary: readonly string[] = PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.includes(permission)
}

/** The entity's permission where its vocabulary has exactly one, so a grant on it has no level to choose. */
export const onlyPermissionOf = (entity: PermissionSetEntity): PermissionSetPermission | null => {
  const vocabulary: readonly PermissionSetPermission[] = PERMISSIONS_BY_ENTITY[entity]
  const [firstPermission, ...otherPermissions] = vocabulary
  return firstPermission != null && otherPermissions.length === 0 ? firstPermission : null
}

/** The entity's permissions, in vocabulary order, filtered to those present in `permissions`. */
export const permissionsOfEntity = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissions: readonly string[]
): PermissionOf<Entity>[] => {
  const vocabulary: readonly PermissionOf<Entity>[] = PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.filter((permission) => permissions.includes(permission))
}

const ORGANISATION_PERMISSIONS_BY_ENTITY = {
  request: [],
  supplier: SUPPLIER_ORGANISATION_PERMISSIONS,
  agreement: [],
  bill: [],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly PermissionOf<Entity>[] }

/** The permissions a set holding any other record permission must also hold, per entity. */
export const MINIMUM_PERMISSIONS_BY_ENTITY = {
  request: ['view'],
  supplier: ['view'],
  agreement: ['view'],
  bill: [],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly PermissionOf<Entity>[] }

type PermissionImplication<Entity extends PermissionSetEntity> = {
  implyingPermission: PermissionOf<Entity>
  impliedPermission: PermissionOf<Entity>
}

/** The permissions a set must also hold because it holds another one, per entity. */
export const IMPLIED_PERMISSIONS_BY_ENTITY = {
  request: [],
  supplier: [{ implyingPermission: 'manage_products', impliedPermission: 'create_product' }],
  agreement: [],
  bill: [],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly PermissionImplication<Entity>[] }

const implicationsApplyingTo = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissions: readonly PermissionSetPermission[]
): readonly PermissionImplication<Entity>[] => {
  const implications: readonly PermissionImplication<Entity>[] = IMPLIED_PERMISSIONS_BY_ENTITY[entity]
  return implications.filter(({ implyingPermission }) => permissions.includes(implyingPermission))
}

/** The permission in `permissions` that makes a set hold `permission`, or null when none implies it. */
export const implyingPermissionOf = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissions: readonly PermissionSetPermission[],
  permission: PermissionSetPermission
): PermissionOf<Entity> | null =>
  implicationsApplyingTo(entity, permissions).find(({ impliedPermission }) => impliedPermission === permission)
    ?.implyingPermission ?? null

/**
 * The permissions a set must also hold because it holds `permissions`: what they imply, and the
 * minimum. Every permission but the minimum itself and the organisation permissions confers the
 * minimum in the model, and an implying permission confers what it implies, so each is held whether
 * or not it is stored.
 */
export const permissionsRequiredBy = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissions: readonly PermissionSetPermission[]
): readonly PermissionOf<Entity>[] => {
  const impliedPermissions = implicationsApplyingTo(entity, permissions).map(({ impliedPermission }) => impliedPermission)
  const minimumPermissions: readonly PermissionOf<Entity>[] = MINIMUM_PERMISSIONS_BY_ENTITY[entity]
  const exemptPermissions: readonly PermissionSetPermission[] = [
    ...minimumPermissions,
    ...ORGANISATION_PERMISSIONS_BY_ENTITY[entity],
  ]
  const isMinimumRequired = [...permissions, ...impliedPermissions].some((permission) => !exemptPermissions.includes(permission))
  return [
    ...(isMinimumRequired ? minimumPermissions : []),
    ...impliedPermissions.filter((permission) => !minimumPermissions.includes(permission)),
  ]
}

/**
 * The entities the record-permission reads answer for: every permission-set entity, plus documents and Insights
 * dashboards, which no permission set governs but whose rows a client still asks about and shares.
 */
export const RECORD_PERMISSIONS_ENTITIES = [...PERMISSION_SET_ENTITIES, 'document', 'insights_dashboard'] as const
export type RecordPermissionsEntity = (typeof RECORD_PERMISSIONS_ENTITIES)[number]

const FGA_DECIDES_IN_EVERY_ORGANISATION: ReadonlySet<RecordPermissionsEntity> = new Set(['insights_dashboard'])

/**
 * Whether OpenFGA answers for the entity's records in every organisation, whatever the organisation's
 * RELEASE_FGA_ENFORCEMENT says. The other entities follow that flag, and the role rules decide them while it is off.
 */
export const isFgaDecidedInEveryOrganisation = (entity: RecordPermissionsEntity): boolean =>
  FGA_DECIDES_IN_EVERY_ORGANISATION.has(entity)

/** The permissions the model answers per record, which need not match what a permission set toggles. */
export const RECORD_PERMISSIONS_BY_ENTITY = {
  request: [
    'view',
    'edit_metadata',
    'edit_po_number',
    'manage_participants',
    'manage_followers',
    'pause',
    'reject',
    'delete',
    'edit_submissions',
    'view_documents',
    'edit_documents',
    'manage_documents',
    'manage_assignees',
    'skip',
    'rollback',
    'manage_integrations',
    'manage_sourcing',
    'administer',
    'view_tprm',
    'edit_tprm',
    'manage_tprm',
    'view_risks',
    'edit_risks',
    'manage_risks',
  ],
  supplier: [
    'view',
    'edit',
    'view_tprm',
    'edit_tprm',
    'manage_tprm',
    'view_subprocessors',
    'edit_subprocessors',
    'manage_subprocessors',
    'view_risks',
    'edit_risks',
    'manage_risks',
    'view_alerts',
    'edit_alerts',
    'view_related_requests',
    'view_related_agreements',
    'view_related_documents',
    'view_related_submissions',
    'edit_documents',
    'manage_documents',
    'manage_internal_contacts',
    'manage_external_contacts',
    'manage_products',
    'create_product',
    'add_agreement',
    'administer',
  ],
  agreement: [
    'view',
    'edit',
    'cancel',
    'delete',
    'view_related_supplier',
    'view_related_requests',
    'manage_renewals',
    'view_documents',
    'edit_documents',
    'manage_documents',
    'administer',
  ],
  bill: ['view', 'edit_before_accept', 'accept', 'assign', 'approve'],
  workflow: ['create_request'],
  document: ['view', 'edit', 'manage'],
  insights_dashboard: ['view', 'edit', 'delete'],
} as const satisfies Record<RecordPermissionsEntity, readonly string[]>

export type RecordPermissionOf<Entity extends RecordPermissionsEntity> = (typeof RECORD_PERMISSIONS_BY_ENTITY)[Entity][number]

export type RecordPermission = RecordPermissionOf<RecordPermissionsEntity>

/**
 * What holding the relation lets a subject do on the record, read from the model rather than
 * restated. Scoped to what the model answers per record, so `can_create` — answered per
 * organisation — is dropped.
 */
export const permissionsForRelations = <Entity extends RecordPermissionsEntity>(
  entity: Entity,
  relations: readonly string[]
): RecordPermissionOf<Entity>[] => {
  const verbsByRelation: Record<string, readonly string[]> = FGA_VERBS_BY_RELATION[entity]
  const conferred = new Set(
    relations.flatMap((relation) => verbsByRelation[relation] ?? []).map((verb) => verb.replace(/^can_/, ''))
  )
  const vocabulary: readonly RecordPermissionOf<Entity>[] = RECORD_PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.filter((permission) => conferred.has(permission))
}
